import {Divider, IconButton, Paper, SvgIcon, Tooltip} from '@mui/material';
import {
    AlignHorizontalCenter,
    AlignHorizontalLeft,
    AlignHorizontalRight,
    AlignVerticalBottom,
    AlignVerticalCenter,
    AlignVerticalTop,
} from '@mui/icons-material';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {ActiveSelection, Canvas, FabricObject, FabricObjectProps, ObjectEvents, SerializedObjectProps,} from 'fabric';
import {ALIGNMENT_OFFSET_GAP} from '../utils/constants';
import {
    Align,
    AlignDirection,
    getAlignmentPlan,
    getFixedGapPlan,
    moveObjectByCanvasDelta,
} from '../utils/alignment-utils';

interface Props {
    selectedElements: FabricObject<
        Partial<FabricObjectProps>,
        SerializedObjectProps,
        ObjectEvents
    >[];
    canvas: Canvas | null;
    refreshElements: () => void;
}


const AlignmentToolbar: React.FC<Props> = ({
                                               selectedElements,
                                               canvas,
                                               refreshElements
                                           }) => {
    const {t} = useTranslation();

    // Read the selected Fabric objects in a stable state before geometry work.
    const getActiveObjectsSafe = useCallback(() => {
        if (!canvas) return null;

        const active = canvas.getActiveObject();
        const objects = canvas.getActiveObjects() as typeof selectedElements;
        if (objects.length < 2) return null;

        const hadActiveSelection = active instanceof ActiveSelection;

        // Measure/move in a stable way: discard selection wrapper first.
        if (hadActiveSelection) canvas.discardActiveObject();

        objects.forEach((o) => o.setCoords());
        return {objects, hadActiveSelection};
    }, [canvas, selectedElements]);

    const finalize = useCallback(
        (objects: FabricObject<any, any, any>[], hadActiveSelection: boolean) => {
            if (!canvas) return;

            if (hadActiveSelection) {
                const sel = new ActiveSelection(objects, {canvas});
                canvas.setActiveObject(sel);
                sel.setCoords();
            }

            canvas.requestRenderAll();
            refreshElements();
        },
        [canvas, refreshElements]
    );

    const handleAlign = useCallback(
        (alignment: Align) => {
            if (!canvas) return;

            const active = getActiveObjectsSafe();
            if (!active) return;

            const {objects, hadActiveSelection} = active;

            // Apply the precomputed canvas deltas for the chosen edge or center alignment.
            for (const move of getAlignmentPlan(objects, alignment)) {
                moveObjectByCanvasDelta(move.object, move.dx, move.dy);
            }

            finalize(objects, hadActiveSelection);
        },
        [canvas, finalize, getActiveObjectsSafe]
    );

    const handleFixedGap = useCallback(
        (dir: AlignDirection) => {
            if (!canvas) return;

            const active = getActiveObjectsSafe();
            if (!active) return;

            const {objects, hadActiveSelection} = active;
            const zoom = canvas.getZoom() || 1;

            // Build one spacing plan first so every object is repositioned from the same snapshot.
            const planned = getFixedGapPlan(objects, dir, ALIGNMENT_OFFSET_GAP, zoom);

            // Apply only the objects that received a spacing move in the plan.
            for (const object of objects) {
                const move = planned.get(object);
                if (!move) continue;
                moveObjectByCanvasDelta(object, move.dx, move.dy);
            }

            finalize(objects, hadActiveSelection);
        },
        [canvas, finalize, getActiveObjectsSafe]
    );

    return (
        <Paper
            elevation={3}
            sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'background.paper',
            }}
        >
            <Tooltip title={t('Align Left')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('left')}>
                    <AlignHorizontalLeft fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Tooltip title={t('Align Center')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('center')}>
                    <AlignHorizontalCenter fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Tooltip title={t('Align Right')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('right')}>
                    <AlignHorizontalRight fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>

            <Tooltip title={t('Align Top')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('top')}>
                    <AlignVerticalTop fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Tooltip title={t('Align Middle')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('middle')}>
                    <AlignVerticalCenter fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Tooltip title={t('Align Bottom')} disableInteractive>
                <IconButton size="small" onClick={() => handleAlign('bottom')}>
                    <AlignVerticalBottom fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>

            <Tooltip
                title={t('Make horizontal spacing even')} disableInteractive>
                <IconButton
                    size="small"
                    onClick={() => handleFixedGap('x')}
                    aria-label={t('Make horizontal spacing even')}
                >
                    <SvgIcon fontSize="small" viewBox="0 0 24 24">
                        <circle cx="6" cy="12" r="2" fill="currentColor"/>
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                        <circle cx="18" cy="12" r="2" fill="currentColor"/>
                    </SvgIcon>
                </IconButton>
            </Tooltip>

            <Tooltip
                title={t('Make vertical spacing even')} disableInteractive>
                <IconButton
                    size="small"
                    onClick={() => handleFixedGap('y')}
                    aria-label={t('Make vertical spacing even')}
                >
                    <SvgIcon fontSize="small" viewBox="0 0 24 24">
                        <circle cx="12" cy="6" r="2" fill="currentColor"/>
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                        <circle cx="12" cy="18" r="2" fill="currentColor"/>
                    </SvgIcon>
                </IconButton>
            </Tooltip>
        </Paper>
    );
};

export default AlignmentToolbar;
